import React from 'react';
import { Alert, Button, RefreshIcon, XMarkIcon } from '../ui';
import {
  formatErrorMessage,
  FormattedError,
} from '../../utils/errorFormatting';
import { AppError } from '../../types/api';

interface ErrorDisplayProps {
  /** The error to display (can be Error object, string, or AppError) */
  error?: Error | AppError | string | unknown;
  /** Context for more specific error messaging */
  context?: string;
  /** Whether to show a retry button */
  showRetry?: boolean;
  /** Callback function for retry action */
  onRetry?: () => void;
  /** Whether to show a close/dismiss button */
  showClose?: boolean;
  /** Callback function for close action */
  onClose?: () => void;
  /** Custom title for the error alert */
  title?: string;
  /** Additional styling className */
  className?: string;
  /** Whether to show the error in a compact format */
  compact?: boolean;
  /** Pre-formatted error object (if you already have a FormattedError) */
  formattedError?: FormattedError;
}

/**
 * Reusable component for displaying user-friendly error messages
 * Uses the error formatting utilities to provide consistent error presentation
 */
const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  context,
  showRetry = false,
  onRetry,
  showClose = false,
  onClose,
  title,
  className = '',
  compact = false,
  formattedError,
}) => {
  // Don't render if no error is provided
  if (!error && !formattedError) {
    return null;
  }

  // Use pre-formatted error or format the error
  const formatted = formattedError || formatErrorMessage(error, context);

  // Determine if retry should be shown based on error type
  const shouldShowRetry = showRetry && formatted.retry && onRetry;

  // Map severity to Alert variant
  const variantMap: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
    error: 'error',
    warning: 'warning',
    info: 'info',
    success: 'success',
  };
  const variant = variantMap[formatted.severity] || 'error';

  const actionContent =
    shouldShowRetry || showClose ? (
      <div className="flex items-center gap-2">
        {shouldShowRetry && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRetry}
            leftIcon={<RefreshIcon className="w-4 h-4" />}
          >
            Try Again
          </Button>
        )}
        {showClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-black/10 transition-colors"
            aria-label="Dismiss"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    ) : undefined;

  return (
    <Alert
      variant={variant}
      title={
        !compact
          ? title ||
            (formatted.severity === 'error'
              ? 'Error'
              : formatted.severity === 'warning'
                ? 'Warning'
                : 'Information')
          : undefined
      }
      action={actionContent}
      className={className}
    >
      <div className={compact ? 'text-sm' : ''}>{formatted.message}</div>
      {formatted.actionable && (
        <div className={`mt-1 opacity-80 ${compact ? 'text-xs' : 'text-sm'}`}>
          {formatted.actionable}
        </div>
      )}
    </Alert>
  );
};

export default ErrorDisplay;

/**
 * Specialized error display for form validation errors
 */
export const FormErrorDisplay: React.FC<{
  error?: Error | AppError | string | unknown;
  onClose?: () => void;
  context?: string;
}> = ({ error, onClose, context = 'submit_form' }) => (
  <ErrorDisplay
    error={error}
    context={context}
    showClose={true}
    onClose={onClose}
    compact={true}
    className="mb-4"
  />
);

/**
 * Specialized error display for data loading errors with retry
 */
export const LoadingErrorDisplay: React.FC<{
  error?: Error | AppError | string | unknown;
  onRetry?: () => void;
  context?: string;
}> = ({ error, onRetry, context = 'load_data' }) => (
  <ErrorDisplay
    error={error}
    context={context}
    showRetry={true}
    onRetry={onRetry}
    className="w-full mb-4"
  />
);

/**
 * Specialized error display for page-level errors
 */
export const PageErrorDisplay: React.FC<{
  error?: Error | AppError | string | unknown;
  context?: string;
  onRetry?: () => void;
}> = ({ error, context, onRetry }) => (
  <ErrorDisplay
    error={error}
    context={context}
    showRetry={!!onRetry}
    onRetry={onRetry}
    className="w-full mb-6"
  />
);
